/**
 * Geocoding service for reverse geocoding using Google Maps Geocoder
 * Uses the Maps JavaScript API Geocoder instead of direct API calls
 */

// Cache for geocoded addresses (keyed by "lat,lng")
const addressCache = new Map();

/**
 * Fetch address using Google Maps Geocoder service
 */
async function fetchAddress(latitude, longitude) {
  if (!window.google?.maps?.Geocoder) {
    throw new Error('Google Maps not loaded');
  }

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      { location: { lat: latitude, lng: longitude } },
      (results, status) => {
        if (status !== 'OK') {
          reject(new Error(`Geocoding error: ${status}`));
          return;
        }

        if (!results || results.length === 0) {
          reject(new Error('No results found'));
          return;
        }

        const result = results[0];
        const components = {};

        // Parse address components
        result.address_components.forEach(component => {
          if (component.types.includes('street_number')) {
            components.house_number = component.long_name;
          } else if (component.types.includes('route')) {
            components.road = component.long_name;
          } else if (component.types.includes('neighborhood') || component.types.includes('sublocality')) {
            components.neighbourhood = component.long_name;
          } else if (component.types.includes('locality')) {
            components.city = component.long_name;
          } else if (component.types.includes('postal_code')) {
            components.postcode = component.long_name;
          }
        });

        const parts = [
          components.house_number,
          components.road,
          components.neighbourhood,
          components.city,
          components.postcode
        ].filter(Boolean);

        resolve({
          formatted: result.formatted_address,
          short: parts.slice(0, 2).join(' ') || result.formatted_address,
          components,
          raw: results
        });
      }
    );
  });
}

/**
 * Reverse geocode a location (lat/lng to address)
 * Uses caching
 *
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<object>} Address object with formatted, short, and components
 */
export async function reverseGeocode(latitude, longitude) {
  // Round to 6 decimal places for cache key (~0.11m precision)
  const cacheKey = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

  // Check cache
  if (addressCache.has(cacheKey)) {
    return addressCache.get(cacheKey);
  }

  // Fetch and cache
  const address = await fetchAddress(latitude, longitude);
  addressCache.set(cacheKey, address);
  return address;
}

/**
 * Batch reverse geocode multiple locations
 *
 * @param {Array<{latitude: number, longitude: number}>} locations
 * @returns {Promise<Array<object>>} Array of address objects
 */
export async function batchReverseGeocode(locations) {
  const promises = locations.map(({ latitude, longitude }) =>
    reverseGeocode(latitude, longitude).catch(error => ({
      error: error.message,
      formatted: 'Address unavailable',
      short: 'N/A'
    }))
  );

  return Promise.all(promises);
}

/**
 * Clear the address cache
 */
export function clearGeocodeCache() {
  addressCache.clear();
}
